/**
 * Générateur — Utiliser les nombres premiers pour simplifier des fractions.
 * Attendu ID : 156 | Niveau : 4ème | Domaine : Divisibilité (desc 10, O20 volet 2)
 *
 * Source doc prof : Ex.4 (décompose 140 et 520 ; simplifie 140/520). Plan de
 * cartes validé 2026-07-14, tel qu'il est depuis la revue du 2026-09-23 :
 *
 *   Carte « irreductible » (clé interne) — Simplifie au maximum (texte_trou_saisie)
 *     a/b = (p·g)/(q·g), p/q irréductible (pgcd = 1) → résultat unique.
 *   Carte « simplifier » — Simplifie par un premier donné (texte_trou_saisie)
 *     a/b divisés par p (facteur commun imposé) → une étape, résultat unique.
 *   Carte « resultat » — Le résultat simplifié au maximum (qcm_unique)
 *     Deux premiers communs dont un de 7 à 19 ; distracteurs nommés
 *     (REGLES_DISTRACTEURS_QCM).
 *
 * Tout exact (pgcd d'Euclide), revérifié par l'énumération des diviseurs
 * communs et les produits, throw sinon.
 *
 * Revue 2026-09-23 (vague 153-163), attendu de fin de 4e : « Il utilise les
 * nombres premiers inférieurs à 100 pour simplifier des fractions. » Exemple de
 * réussite : « Il simplifie 140/135. » Cours du prof (19Fractions1) :
 * « Simplifier au maximum la fraction 153/85 » par décomposition
 * (153 = 3 × 3 × 17, 85 = 5 × 17) ; 1 470/1 680 = 7/8 par divisions successives.
 *   - Vocabulaire : « irréductible » est le mot et la notion de 3e (Eduscol :
 *     « La notion de fraction irréductible est introduite en classe de 3e » ;
 *     attendus de fin de 3e « pour la rendre irréductible ») ; on écrit « au
 *     maximum », comme le cours. Mode auto, À ARBITRER. À noter : 147 et 149,
 *     déjà revus, emploient encore « irréductible ».
 *   - « irreductible » (clé gardée, elle ne s'affiche pas) : libellé
 *     « Simplifier une fraction au maximum », consigne « Simplifie cette
 *     fraction au maximum. », sans parenthèse ; phrase en ligne KaTeX
 *     (fractions empilées, fracTrouTex). Facteur commun g recadré : avant, de
 *     2 à 9, jamais un premier au-delà de 7 (le niveau de l'automatisme de 3e
 *     1222, « dans une même table ») ; désormais produit de 1 à 3 premiers
 *     parmi 2, 3, 5, 7 (défaut) ou, un tirage sur trois (PART_FACTEUR_GRAND),
 *     un premier de 11 à 19 éventuellement multiplié par 2 ou 3 (153/85 via
 *     17), marqué contenu.difficulte = 'facteur_premier_grand' ; p/q
 *     irréductible, q ≥ 2, p, q ≤ 30 ; termes ≤ 1 000.
 *   - « simplifier » : premier imposé parmi 2, 3, 5, 7, 11, 13, 17 (avant 2 à 7
 *     seulement) ; x = y exclu (« 15/15 = 5/5 », 9,3 % avant) ; consigne sans
 *     la parenthèse qui donnait la méthode ; ligne KaTeX.
 *   - « estirreductible » SORTIE : la taille des termes donnait la clé dans
 *     96,3 % des tirages (oui si les deux termes sont ≤ 12), et « décider si une
 *     fraction est irréductible » est la carte 206 de 3e.
 *   - « resultat » NOUVELLE, pour garder un QCM à l'attendu (seul format
 *     dépouillable en classe au QR code) : « Simplifie au maximum la fraction
 *     a/b. Quel résultat obtiens-tu ? », deux premiers communs dont un de 7 à
 *     19 ; familles simplification_partielle, un_seul_terme, deux_coefficients.
 *     Mesures (tmp-revue-156, 10 000 tirages) : « les plus petits termes » ne
 *     désigne jamais la clé (c'est deux_coefficients), « l'option qui partage
 *     le plus de termes » la met toujours à égalité avec deux autres. RÉSIDU :
 *     trois options ont le dénominateur q, et la clé y a toujours le
 *     numérateur du milieu (100 %) ; la sortir du milieu la rendrait la plus
 *     petite, donc trouvable par « simplifiée = plus petits nombres ». Choix
 *     du correcteur, à arbitrer. Relecture du coordinateur : le numérateur p
 *     est tiré COMPOSÉ, sinon deux_coefficients divisait p par lui-même et
 *     proposait 1/q (805/420 → 1/12), une option que personne ne choisit.
 *   - « forme » SORTIE : même geste et mêmes 632 couples que la carte à trous,
 *     et « 7 / 2 » tapé avec des espaces était refusé à 100 %.
 *   - Rappel brouillon IMPÉRATIF sur les trois cartes (RAPPEL_BROUILLON).
 */

import {
    randomChoice, randInt, fracTex, fracTrouTex,
    makeQcm, makeTexteTrou,
} from '../../../utils';
import { pgcd } from './premiers';

const ATTENDU_ID = 156;

const L_IRRED = 'Simplifier une fraction au maximum';
const L_SIMPL = 'Simplifier par un facteur premier';
const L_RESULTAT = 'Trouver la fraction simplifiée au maximum';

const opts = (label) => ({ niveau: '4eme', matiere: 'maths', attendu: ATTENDU_ID, label });

// Rappel brouillon (décision 2026-09-23), IMPÉRATIF sur les trois cartes :
// trouver les facteurs communs demande des divisions. Forme maison (147, 149) ;
// juste avant la question ; aucune méthode soufflée.
const RAPPEL_BROUILLON = '<p class="italic text-slate-400">✏️ Pose tes calculs au brouillon.</p>';

const TERME_MAX = 1000;

// Second chemin : le plus grand diviseur commun par énumération (jamais
// l'algorithme d'Euclide de premiers.js).
const plusGrandCommun = (a, b) => {
    let g = 1;
    for (let d = 2; d <= Math.min(a, b); d++) if (a % d === 0 && b % d === 0) g = d;
    return g;
};
const memeValeur = ([n1, d1], [n2, d2]) => n1 * d2 === n2 * d1;

// Les facteurs premiers distincts de n ≥ 2 (divisions successives).
const facteursPremiers = (n) => {
    const out = [];
    let m = n;
    for (let d = 2; m > 1; d++) {
        if (m % d === 0) {
            out.push(d);
            while (m % d === 0) m /= d;
        }
    }
    return out;
};

// p/q irréductible, 1 ≤ p ≤ 30, 2 ≤ q ≤ 30, p ≠ q, termes p·g et q·g ≤ 1 000 ;
// `accepteP` filtre le numérateur (la carte « resultat » le veut composé).
const tirerBase = (g, pMin = 1, accepteP = () => true) => {
    const bases = [];
    for (let q = 2; q <= 30 && q * g <= TERME_MAX; q++) {
        for (let p = pMin; p <= 30 && p * g <= TERME_MAX; p++) {
            if (p !== q && pgcd(p, q) === 1 && accepteP(p)) bases.push({ p, q });
        }
    }
    return bases.length ? randomChoice(bases) : null;
};

// ═════════ Carte « irreductible » — Simplifie au maximum (trous) ═════════

// ── Échelon « facteur_premier_grand » (2026-09-23) ──────────────────────────
// Part des tirages dont le facteur commun contient un premier de 11 à 19 : un
// sur trois. Le professeur change ce nombre ici et nulle part ailleurs.
const PART_FACTEUR_GRAND = 1 / 3;
const DIFFICULTE_GRAND = 'facteur_premier_grand';

/**
 * Les échelons de la carte « irreductible », DU PLUS SIMPLE AU PLUS COÛTEUX
 * (FORMATS_TANOED §0.8). Donnée pure, jamais lue à l'exécution. `pose: false` :
 * l'échelon sort des tirages mais rien ne le marque (c'est le défaut).
 */
export const ECHELONS_DIFFICULTE = [
    {
        code: 'facteurs_des_tables',
        pose: false,
        geste: 'le facteur commun est un produit de 1 à 3 premiers parmi 2, 3, 5, 7 : les critères de divisibilité et les tables suffisent, en une ou plusieurs étapes',
    },
    {
        code: DIFFICULTE_GRAND,
        pose: true,
        geste: 'le facteur commun contient un premier de 11 à 19 (153/85 = 9/5 par 17) : il faut décomposer, ou connaître la liste des premiers, pour le voir',
    },
];

const tirerFacteurCommun = () => {
    if (Math.random() < PART_FACTEUR_GRAND) {
        return { g: randomChoice([11, 13, 17, 19]) * randomChoice([1, 2, 3]), grand: true };
    }
    let g = 1;
    for (let k = randInt(1, 3); k > 0; k--) g *= randomChoice([2, 3, 5, 7]);
    return { g, grand: false };
};

const genIrreductibleTrous = () => {
    let tirage = null;
    for (let essai = 0; essai < 40 && !tirage; essai++) {
        const { g, grand } = tirerFacteurCommun();
        const base = tirerBase(g);
        if (base) tirage = { ...base, g, grand };
    }
    if (!tirage) tirage = { p: 9, q: 5, g: 17, grand: true };          // repli : 153/85 = 9/5 (le cours)
    const { p, q, g, grand } = tirage;
    const a = p * g;
    const b = q * g;
    if (plusGrandCommun(a, b) !== g || !memeValeur([a, b], [p, q])) throw new Error(`gen_156 irreductible : ${a}/${b} incohérent`);
    const payload = makeTexteTrou(`$\\dfrac{${a}}{${b}} = ${fracTrouTex(`{{${p}}}`, `{{${q}}}`)}$`,
        RAPPEL_BROUILLON
        + '<p><b>Simplifie cette fraction au maximum.</b></p>',
        { ...opts(L_IRRED), mode: 'saisie' });
    payload._meta.params = { a, b, p, q, g };
    if (grand) {
        payload.contenu = { ...payload.contenu, difficulte: DIFFICULTE_GRAND };
        payload._meta.params.difficulte = DIFFICULTE_GRAND;
    }
    return payload;
};

// ═════════ Carte « simplifier » — Par un facteur premier donné (trous) ═════════

const genSimplifierTrous = () => {
    const p = randomChoice([2, 3, 5, 7, 11, 13, 17]);
    const x = randInt(2, 12);
    const y = randomChoice([2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].filter((v) => v !== x));
    const a = p * x;
    const b = p * y;
    if (a % p || b % p || !memeValeur([a, b], [x, y])) throw new Error(`gen_156 simplifier : ${a}/${b} incohérent`);
    const payload = makeTexteTrou(`$\\dfrac{${a}}{${b}} = ${fracTrouTex(`{{${x}}}`, `{{${y}}}`)}$`,
        RAPPEL_BROUILLON
        + `<p><b>Simplifie cette fraction par ${p}.</b></p>`,
        { ...opts(L_SIMPL), mode: 'saisie' });
    payload._meta.params = { a, b, p, x, y };
    return payload;
};

// ═════════ Carte « resultat » — Le résultat simplifié au maximum (QCM) ═════════

export const REGLES_DISTRACTEURS_QCM = {
    resultat_qcm_unique: {
        support: 'a/b = (p·s·P)/(q·s·P) : p/q irréductible (p COMPOSÉ de 4 à 30, 2 ≤ q ≤ 30, p ≠ q), s premier parmi 2, 3, 5 et P premier de 7 à 19, termes ≤ 1 000 ; la clé est p/q. p est composé pour que deux_coefficients garde un numérateur p/y ≥ 2 : avec p premier, y = p et l\'option devenait 1/q (805/420 proposait 1/12), un choix que personne ne fait',
        familles: {
            simplification_partielle: '(p·P)/(q·P) : simplifiée par le petit premier commun s seulement, le premier P de 7 à 19 n\'est pas vu (le cœur de l\'attendu) ; même valeur, pas « au maximum »',
            un_seul_terme: 'a/q : le dénominateur est divisé par le facteur commun, le numérateur est oublié',
            deux_coefficients: '(p/y)/q, y facteur premier de p : le numérateur divisé par s·P·y et le dénominateur par s·P, deux nombres différents',
        },
        supprimees: {
            est_irreductible: 'l\'ancienne carte « Reconnaître une fraction irréductible » sort (3e, carte 206 ; clé lue à la taille des termes dans 96,3 % des tirages)',
        },
        garantie: 'quatre choix sans boucle de complément : la clé p/q, puis (p·P)/(q·P), a/q et (p/y)/q, deux à deux distincts (dénominateurs q·P ≠ q, et numérateurs p, a = p·s·P, p/y deux à deux différents) ; seule la clé est égale à a/b ET simplifiée au maximum : (p·P)/(q·P) a la même valeur mais se simplifie encore par P, a/q et (p/y)/q n\'ont pas la valeur de a/b ; revérifié par les produits et l\'énumération des diviseurs communs, throw sinon. Trois choix partagent le dénominateur q : ni « les plus petits termes » (c\'est (p/y)/q) ni « le terme partagé par le plus d\'options » ne désignent la clé seule',
    },
};

const genResultatQcm = () => {
    let tirage = null;
    for (let essai = 0; essai < 40 && !tirage; essai++) {
        const s = randomChoice([2, 3, 5]);
        const P = randomChoice([7, 11, 13, 17, 19]);
        const base = tirerBase(s * P, 2, (n) => facteursPremiers(n)[0] !== n);   // n composé
        if (base) tirage = { ...base, s, P };
    }
    if (!tirage) tirage = { p: 9, q: 5, s: 2, P: 17 };                 // repli : 306/170 = 9/5
    const { p, q, s, P } = tirage;
    const g = s * P;
    const a = p * g;
    const b = q * g;
    const y = randomChoice(facteursPremiers(p));
    const cle = [p, q];
    const options = {
        simplification_partielle: [p * P, q * P],
        un_seul_terme: [a, q],
        deux_coefficients: [p / y, q],
    };
    // Second chemin : la clé seule est égale à a/b et sans diviseur commun.
    const vals = Object.values(options);
    if (plusGrandCommun(a, b) !== g || !memeValeur(cle, [a, b]) || plusGrandCommun(p, q) !== 1
        || !memeValeur(options.simplification_partielle, [a, b]) || plusGrandCommun(p * P, q * P) === 1
        || memeValeur(options.un_seul_terme, [a, b]) || memeValeur(options.deux_coefficients, [a, b])
        || new Set([cle, ...vals].map((f) => f.join('/'))).size !== 4) {
        throw new Error(`gen_156 resultat : ${a}/${b} incohérent`);
    }
    const affiche = ([n, d]) => fracTex(n, d);
    const familles = Object.keys(options);
    const payload = makeQcm([cle, ...vals].map(affiche), affiche(cle),
        RAPPEL_BROUILLON
        + `<p><b>Simplifie au maximum la fraction ${fracTex(a, b)}. Quel résultat obtiens-tu ?</b></p>`,
        opts(L_RESULTAT));
    payload._meta.params = { a, b, p, q, s, P, y, distracteurs: familles };
    return payload;
};

// ─── Registre ────────────────────────────────────────────────
const GENERATORS = {
    irreductible_texte_trou_saisie: genIrreductibleTrous,
    simplifier_texte_trou_saisie: genSimplifierTrous,
    resultat_qcm_unique: genResultatQcm,
};

const FORMAT_LABELS = {
    texte_trou_saisie: 'Saisie trous', qcm_unique: 'QCM',
};
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'irreductible', label: L_IRRED, formatsList: buildFormats('irreductible') },
    { id: 'simplifier', label: L_SIMPL, formatsList: buildFormats('simplifier') },
    { id: 'resultat', label: L_RESULTAT, formatsList: buildFormats('resultat') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
